/**
 * Button - Interactive UI button from the Game UI Kit. Supports variant, size,
 * disabled, and loading states with an optional click handler.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'success'
  | 'danger'
  | 'ghost'
  | 'outline';
export type ButtonSize = 'small' | 'medium' | 'large' | 'extra-large';
export type ButtonLoadingStyle = 'spinner';

export interface ButtonOptions extends BaseUIComponentOptions {
  /** Text shown inside the button */
  label?: string;
  /** Visual variant matching the Game UI Kit palette */
  variant?: ButtonVariant;
  /** Predefined size */
  size?: ButtonSize;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Click handler. Ignored while disabled or loading. */
  onClick?: (event: MouseEvent) => void;
}

export class Button extends BaseUIComponent<ButtonOptions> {
  /** Catalog metadata consumed by the genesys-ui-kit catalog generator. */
  public static metadata: UIComponentMetadata = {
    displayName: 'Button',
    category: 'control',
    summary: 'Interactive button with variant, size, disabled, and loading states.',
    useCases: ['confirm', 'cancel', 'submit', 'menu action', 'call to action', 'cta'],
    optionsType: 'ButtonOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/button.html',
      styles: '@engine/assets/ui/styles/button.css',
    },
  };

  private buttonElement: HTMLButtonElement | null = null;
  private labelElement: HTMLElement | null = null;
  private clickHandler: ((event: MouseEvent) => void) | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Button.metadata.assetPaths.template,
      stylesPath: Button.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<ButtonOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      label: 'Button',
      variant: 'primary',
      size: 'medium',
      disabled: false,
      loading: false,
      onClick: () => { /* no-op */ },
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      label: this.options.label,
      variant: this.options.variant,
      size: this.options.size,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;

    this.buttonElement = this.layout.querySelector('.ui-button') as HTMLButtonElement | null;
    this.labelElement = this.layout.querySelector('.ui-button-label') as HTMLElement | null;

    if (this.buttonElement) {
      const wrapper = this.createElementWrapper(this.buttonElement);
      this.elements.set('button', wrapper);
    }
    if (this.labelElement) {
      const wrapper = this.createElementWrapper(this.labelElement);
      this.elements.set('label', wrapper);
    }
  }

  protected override async onInitialize(): Promise<void> {
    this.applyDisabled();
    this.applyLoading();
    this.attachClickListener();
  }

  private attachClickListener(): void {
    if (!this.buttonElement) return;
    this.clickHandler = (event: MouseEvent) => {
      if (this.options.disabled || this.options.loading) return;
      this.options.onClick(event);
    };
    this.buttonElement.addEventListener('click', this.clickHandler);
  }

  /** Update the button label text. */
  public setLabel(label: string): void {
    this.options.label = label;
    if (this.labelElement) this.labelElement.textContent = label;
  }

  /** Switch the visual variant. */
  public setVariant(variant: ButtonVariant): void {
    if (!this.buttonElement) return;
    this.buttonElement.classList.remove(`variant-${this.options.variant}`);
    this.options.variant = variant;
    this.buttonElement.classList.add(`variant-${variant}`);
  }

  /** Switch the size preset. */
  public setSize(size: ButtonSize): void {
    if (!this.buttonElement) return;
    this.buttonElement.classList.remove(`size-${this.options.size}`);
    this.options.size = size;
    this.buttonElement.classList.add(`size-${size}`);
  }

  /** Enable / disable the button. */
  public setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    this.applyDisabled();
  }

  /** Toggle the loading state. */
  public setLoading(loading: boolean): void {
    this.options.loading = loading;
    this.applyLoading();
  }

  /** Replace the click handler. */
  public setOnClick(handler: (event: MouseEvent) => void): void {
    this.options.onClick = handler;
  }

  private applyDisabled(): void {
    if (!this.buttonElement) return;
    this.buttonElement.disabled = this.options.disabled;
  }

  private applyLoading(): void {
    if (!this.buttonElement) return;
    this.buttonElement.dataset.loading = this.options.loading ? 'true' : 'false';
  }

  protected override onDestroy(): void {
    if (this.buttonElement && this.clickHandler) {
      this.buttonElement.removeEventListener('click', this.clickHandler);
    }
    this.clickHandler = null;
    this.buttonElement = null;
    this.labelElement = null;
  }
}

